import React from 'react';
import { Spinner } from 'react-bootstrap';
import QRCode from 'react-qr-code';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { ServicesContext } from '~/services';

export type GoogleAuthSecretType = { secret: string; qrCode: string };
export interface GoogleAuthSetupProps {
    googleAuthSecret: GoogleAuthSecretType;
    setGoogleAuthVerified: (x: boolean) => void;
    setGoogleAuthSecret: (x: undefined | GoogleAuthSecretType) => void;
    onMfaUpdate?: () => void;
}
export function GoogleAuthSetup({
    googleAuthSecret,
    setGoogleAuthVerified,
    setGoogleAuthSecret,
    onMfaUpdate,
}: GoogleAuthSetupProps) {
    const services = React.useContext(ServicesContext);
    const [code, setCode] = React.useState('');
    const [validation, setValidation] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    async function handleSubmit() {
        try {
            setLoading(true);
            setValidation(false);
            // verify code, but don't perform login steps
            await services.user.totpVerify(code, 'google');
            setGoogleAuthVerified(true);
            setGoogleAuthSecret(undefined);
            setCode('');
            if (onMfaUpdate) {
                onMfaUpdate();
            }
        } catch (e) {
            setValidation(true);
        } finally {
            setLoading(false);
        }
    }

    function cancel() {
        setCode('');
        setValidation(false);
        setGoogleAuthSecret(undefined);
    }

    return (
        <div>
            <p>Google Authenticator</p>
            <p>
                To begin, download Google Authenticator from the{' '}
                <a
                    href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                    target="_blank"
                    rel="noreferrer"
                >
                    Google Play Store
                </a>{' '}
                or the{' '}
                <a
                    href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                    target="_blank"
                    rel="noreferrer"
                >
                    iOS App Store
                </a>
                .
            </p>
            <p>
                Step 1: In the Google Authenticator app, tap <b>Add a code</b> and then choose{' '}
                <b>Scan a QR code</b> or <b>Enter a setup key</b>.
            </p>
            <p>
                Step 2: Scan the QR code below or manually type the setup key into the authenticator
                app.
            </p>
            <div className="text-center">
                <QRCode size={120} value={googleAuthSecret.qrCode} />
                <p className="mt-3">
                    <b>{googleAuthSecret.secret.match(/.{4}/g)!.join(' ')}</b>
                </p>
            </div>
            <p>Step 3: Enter the 6 digit code you see in your authenticator app.</p>
            <div>
                <Form onSubmit={handleSubmit} values={{ code }} autoComplete="off">
                    <FormGroup htmlFor="verification-code">
                        <div className="d-flex align-items-center">
                            <label htmlFor="verification-code">Code</label>
                            <input
                                id="verification-code"
                                type="text"
                                className="form-control ms-2"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                maxLength={6}
                            />
                        </div>
                        <ValidationMessage
                            className="col-12 mt-2 text-center"
                            type="external"
                            rule={validation}
                        >
                            The verification code you entered is incorrect.
                        </ValidationMessage>
                    </FormGroup>
                    <div className="text-end mt-3">
                        <button className="btn btn-secondary me-2" type="button" onClick={cancel}>
                            Cancel
                        </button>
                        <button className="btn btn-primary" type="submit" disabled={loading}>
                            {loading && (
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    className="me-2"
                                />
                            )}
                            Verify Code
                        </button>
                    </div>
                </Form>
            </div>
        </div>
    );
}
